import { Link } from 'react-router-dom';
import { useState } from 'react';
import BrandLogo from '@/components/base/BrandLogo';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { supabase } from '@/lib/supabase';

export default function Footer() {
  const { data: settings } = useSiteSettings();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [msg, setMsg] = useState('');
  const [honeypot, setHoneypot] = useState('');

  const churchName = settings?.church_name || 'TGMCI';
  const fullName = settings?.full_name || 'The Great Multitude Church Int\u2019l';
  const locations = settings?.locations ?? [];
  const phones = settings?.phones ?? [];
  const emails = settings?.emails ?? [];
  const social = settings?.social ?? { facebook: '#', twitter: '#', instagram: '#', youtube: '#' };

  const handleNewsletter = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (honeypot.trim()) {
      setStatus('success');
      setMsg('Thank you for subscribing!');
      setEmail('');
      return;
    }
    if (!email) return;
    setStatus('loading');

    const { error } = await supabase.from('newsletter_subscribers').insert({ email });

    if (error) {
      // Duplicate email (unique constraint) is not a real failure for the user.
      if (error.code === '23505') {
        setStatus('success');
        setMsg('You are already subscribed!');
        setEmail('');
      } else {
        setStatus('error');
        setMsg('Something went wrong. Please try again.');
      }
    } else {
      setStatus('success');
      setMsg('Thank you for subscribing!');
      setEmail('');
    }
  };

  const socialLinks = [
    { icon: 'ri-facebook-fill', href: social.facebook, label: 'Facebook' },
    { icon: 'ri-twitter-x-fill', href: social.twitter, label: 'Twitter/X' },
    { icon: 'ri-instagram-line', href: social.instagram, label: 'Instagram' },
    { icon: 'ri-youtube-fill', href: social.youtube, label: 'YouTube' },
  ];

  return (
    <footer className="bg-foreground-950 text-white">
      {/* Newsletter Strip */}
      <div className="bg-primary-700 py-10 px-4">
        <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-xl font-semibold text-white">Stay Connected with Our Community</h3>
            <p className="text-white/70 text-sm mt-1">Subscribe to our newsletter for updates, sermons, and events.</p>
          </div>
          <form onSubmit={handleNewsletter} className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </div>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              required
              className="px-4 py-3 rounded-md text-sm text-foreground-900 bg-white placeholder-foreground-400 outline-none focus:ring-2 focus:ring-accent-400 w-full sm:w-72"
              aria-label="Email address for newsletter"
            />
            <button type="submit" disabled={status === 'loading'} className="btn-accent whitespace-nowrap">
              {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
          {status === 'success' && <p className="text-accent-300 text-sm font-medium">{msg}</p>}
          {status === 'error' && <p className="text-red-300 text-sm">{msg}</p>}
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-16 px-4">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Column 1 — About */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <BrandLogo url={settings?.logo_url} name={churchName} settings={settings?.logo_settings} className="!w-12 !h-12" />
              <div>
                <div className="font-heading font-bold text-lg text-white">{churchName}</div>
                <div className="text-xs text-foreground-400 leading-tight">{fullName}</div>
              </div>
            </div>
            <p className="text-sm text-foreground-400 leading-relaxed mb-5">
              {settings?.tagline || 'Empowering youth through faith and community in Edea, Cameroon and Accra, Ghana.'}
            </p>
            <div className="flex items-center gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.icon}
                  href={s.href || '#'}
                  aria-label={s.label}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground-800 hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className={`${s.icon} text-sm`} aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2 — Locations */}
          <div>
            <h4 className="font-label font-semibold text-white uppercase tracking-widest text-xs mb-5">
              <a href="#locations" className="hover:text-accent-400 transition-colors">Locations</a>
            </h4>
            <ul className="space-y-4 text-sm text-foreground-400">
              {locations.map((loc) => (
                <li key={loc.label}>
                  <strong className="text-foreground-300 block">{loc.label}</strong>
                  <span>{loc.address}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 — Helpful Links */}
          <div>
            <h4 className="font-label font-semibold text-white uppercase tracking-widest text-xs mb-5">
              <a href="#links" className="hover:text-accent-400 transition-colors">Helpful Links</a>
            </h4>
            <ul className="space-y-2.5">
              {[
                { label: 'About TGMCI', path: '/about' },
                { label: 'Our Services', path: '/services' },
                { label: 'Sermons & Media', path: '/sermons' },
                { label: 'Events', path: '/events' },
                { label: 'Watch Live', path: '/live' },
                { label: 'Give / Support TGMCI', path: '/give' },
                { label: 'Books', path: '/books' },
                { label: 'Highlights Gallery', path: '/highlights' },
                { label: 'Testimonials', path: '/testimonials' },
                { label: 'News & Updates', path: '/news' },
                { label: 'Get in Touch', path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-foreground-400 hover:text-accent-400 transition-colors flex items-center gap-1.5"
                  >
                    <i className="ri-arrow-right-s-line text-xs" aria-hidden="true" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 — Contact */}
          <div>
            <h4 className="font-label font-semibold text-white uppercase tracking-widest text-xs mb-5">
              <a href="/contact" className="hover:text-accent-400 transition-colors">Get in Touch</a>
            </h4>
            <ul className="space-y-3 text-sm text-foreground-400">
              {phones.map((p) => (
                <li key={p.number} className="flex items-start gap-2">
                  <i className="ri-phone-line text-accent-500 mt-0.5 shrink-0" aria-hidden="true" />
                  <div>
                    <span className="text-foreground-300 text-xs uppercase">{p.label}:</span>
                    <a href={`tel:${p.number}`} className="block hover:text-accent-400 transition-colors">
                      {p.number}
                    </a>
                  </div>
                </li>
              ))}
              <li className="pt-2">
                <strong className="text-foreground-300 text-xs uppercase block mb-2">Emails</strong>
                {emails.map((em) => (
                  <a
                    key={em}
                    href={`mailto:${em}`}
                    className="flex items-center gap-2 hover:text-accent-400 transition-colors mb-1"
                  >
                    <i className="ri-mail-line text-accent-500 shrink-0" aria-hidden="true" />
                    {em}
                  </a>
                ))}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-foreground-800 py-6 px-4">
        <div className="max-w-[1200px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-foreground-500">
          <p>Copyright &copy; {new Date().getFullYear()} {fullName}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-accent-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-accent-400 transition-colors">Terms of Use</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}